import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, Import } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/lib/adminApi";
import { PageHeader, Panel, inputCls, btnPrimary, btnSecondary, money } from "@/components/admin/ui";
import { PartyFields, LinesEditor, DocPreview, computePreview, emptyLine, CustomerPicker } from "@/components/admin/billing";

const emptyParty = { name: "", email: "", phone: "", street: "", house_num: "", pcode: "", city: "", country: "CH" };

export default function AdminBillingForm({ kind }) {
  const isQuote = kind === "quote";
  const { id } = useParams(); // édition si présent
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [customer, setCustomer] = useState(emptyParty);
  const [lines, setLines] = useState([emptyLine()]);
  const [language, setLanguage] = useState("fr");
  const [billing, setBilling] = useState({ po_number: "", billing_email: "", cost_center: "" });
  const [bookingIds, setBookingIds] = useState([]);
  const [serviceInfo, setServiceInfo] = useState(null);
  const [importCode, setImportCode] = useState("");
  const [importError, setImportError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [dueInDays, setDueInDays] = useState("");
  const [validityDays, setValidityDays] = useState("");
  const [terms, setTerms] = useState("");
  const [notes, setNotes] = useState("");
  const [conditions, setConditions] = useState("");
  const [transport, setTransport] = useState({});
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.get("/settings").then((r) => setSettings(r.data.settings)).catch(() => {});
    adminApi.get("/customers?per_page=100").then((r) => setCustomers(r.data.customers || [])).catch(() => {});
    if (id) {
      adminApi.get(`/${isQuote ? "quotes" : "invoices"}/${id}`).then((r) => {
        const doc = isQuote ? r.data.quote : r.data.invoice;
        if (!isQuote && doc.status !== "draft") {
          setLocked(true);
          toast.info("Facture émise — lecture seule (créez un avoir pour corriger)");
        }
        setCustomer({ ...emptyParty, ...doc.customer });
        setLines(doc.lines?.length ? doc.lines : [emptyLine()]);
        setLanguage(doc.language || "fr");
        setBilling({ po_number: doc.billing?.po_number || "", billing_email: doc.billing?.billing_email || "", cost_center: doc.billing?.cost_center || "" });
        setBookingIds(doc.booking_ids || (doc.booking_id ? [doc.booking_id] : []));
        setServiceInfo(doc.service_info || null);
        setDueDate(doc.due_date || "");
        setDueInDays(doc.due_in_days || "");
        setTerms(doc.terms || "");
        setNotes(doc.notes || "");
        setConditions(doc.conditions || "");
        setTransport(doc.transport || {});
      }).catch(() => toast.error("Document introuvable"));
    }
  }, [id, isQuote]);

  const totals = useMemo(
    () => computePreview(lines, settings?.billing?.vat_registered !== false, settings?.billing?.vat_rate || 8.1),
    [lines, settings]);

  const importBooking = async () => {
    const code = importCode.trim().toUpperCase();
    if (!code) return;
    setImportError("");
    try {
      const search = await adminApi.get(`/bookings?q=${encodeURIComponent(code)}`);
      const booking = (search.data.bookings || []).find((b) => b.tracking_code === code) || (search.data.bookings || [])[0];
      if (!booking) { setImportError(`Aucune réservation trouvée pour « ${code} »`); return; }
      const r = await adminApi.get(`/invoices/import/${booking.id}`);
      setCustomer((c) => ({ ...c, ...Object.fromEntries(Object.entries(r.data.customer).filter(([, v]) => v)) }));
      setLines(r.data.lines.map((l) => ({ ...emptyLine(), ...l })));
      setBookingIds(r.data.booking_ids);
      setServiceInfo(r.data.service_info);
      if (isQuote) {
        setTransport({
          date: booking.date, time: booking.time, return_date: booking.return_date, return_time: booking.return_time,
          pickup: booking.pickup, dropoff: booking.dropoff, passengers: booking.passengers, luggage: booking.luggage,
          vehicle_category: booking.vehicle_category,
          passenger_name: `${booking.customer?.first_name || ""} ${booking.customer?.last_name || ""}`.trim(),
        });
      }
      toast.success(`Réservation ${code} importée`);
    } catch { setImportError("Import impossible — vérifiez le code et réessayez"); }
  };

  const pickCustomer = (c) => {
    setCustomer((prev) => ({
      ...prev,
      name: c.company || `${c.first_name || ""} ${c.last_name || ""}`.trim(),
      email: c.email || "", phone: c.phone || "",
      street: c.street || prev.street, house_num: c.house_num || prev.house_num,
      pcode: c.pcode || prev.pcode, city: c.city || prev.city, country: c.country || prev.country,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (locked || busy) return;
    if (!customer.name.trim()) { toast.error("Le nom du client est requis"); return; }
    const cleanLines = lines.filter((l) => l.description.trim());
    if (!cleanLines.length) { toast.error("Ajoutez au moins une ligne"); return; }
    setBusy(true);
    const payload = {
      customer, language, billing, booking_ids: bookingIds,
      lines: cleanLines.map((l) => ({
        description: l.description, qty: Number(l.qty) || 1,
        unit_price: Number(l.unit_price) || 0,
        vat_rate: l.vat_rate === null || l.vat_rate === "" ? null : Number(l.vat_rate),
        discount_type: l.discount_type || null,
        discount_value: Number(l.discount_value) || 0,
      })),
      due_in_days: dueInDays ? Number(dueInDays) : null, due_date: dueDate || null,
      terms: terms || null, notes: notes || null,
      ...(isQuote ? { conditions: conditions || null, validity_days: validityDays ? Number(validityDays) : null,
                      transport: Object.keys(transport).length ? transport : null } : {}),
    };
    try {
      if (id) {
        await adminApi.patch(`/${isQuote ? "quotes" : "invoices"}/${id}`, payload);
        toast.success(isQuote ? "Devis mis à jour" : "Facture mise à jour");
        navigate(`/admin/app/${isQuote ? "quotes" : "invoices"}/${id}`);
      } else {
        const r = await adminApi.post(`/${isQuote ? "quotes" : "invoices"}`, payload);
        toast.success(isQuote ? "Devis créé" : "Facture créée (brouillon)");
        navigate(`/admin/app/${isQuote ? "quotes" : "invoices"}/${r.data[isQuote ? "quote" : "invoice"].id}`);
      }
    } catch (err) {
      toast.error(apiError(err, "Enregistrement impossible"));
    } finally {
      setBusy(false);
    }
  };

  const t = (k) => (e) => setTransport({ ...transport, [k]: e.target.value });
  const listPath = `/admin/app/${isQuote ? "quotes" : "invoices"}`;

  return (
    <div data-testid={`billing-form-${kind}`}>
      <Link to={listPath} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate transition-colors hover:text-ink">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {isQuote ? "Devis" : "Factures"}
      </Link>
      <PageHeader title={id ? (isQuote ? "Modifier le devis" : "Modifier la facture") : (isQuote ? "Nouveau devis" : "Nouvelle facture")}
        subtitle={isQuote ? "Proposition commerciale — le client pourra l'accepter en ligne" : "Le numéro, la référence SCOR et le Swiss QR sont générés à l'émission"} />

      <form onSubmit={submit} className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          <Panel title="Client" testId="form-customer">
            {customers.length > 0 && (
              <div className="mb-3">
                <label className="text-xs font-medium text-slate">Client existant</label>
                <CustomerPicker customers={customers} onPick={pickCustomer} />
              </div>
            )}
            <PartyFields value={customer} onChange={setCustomer} testId="customer" />
            <div className="mt-4 border-t border-ink/[0.06] pt-4">
              <label className="text-xs font-medium text-slate">Importer une réservation</label>
              <div className="mt-1 flex gap-2">
                <input value={importCode} onChange={(e) => { setImportCode(e.target.value); setImportError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); importBooking(); } }}
                  placeholder="ML-XXXXX" data-testid="import-code" className={`${inputCls} font-mono uppercase`} />
                <button type="button" onClick={importBooking} disabled={!importCode.trim()} data-testid="import-booking-button"
                  className={`${btnSecondary} disabled:cursor-not-allowed disabled:opacity-40`}>
                  <Import className="h-4 w-4" strokeWidth={1.5} /> Importer</button>
              </div>
              {importError && (
                <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600" data-testid="import-error">{importError}</p>
              )}
              {serviceInfo?.booking_ref && (
                <p className="mt-2 rounded-lg bg-cloud px-3 py-2 text-xs text-slate" data-testid="import-success">
                  Liée à {serviceInfo.booking_ref} · {serviceInfo.route}</p>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-ink/[0.06] pt-4">
              <div>
                <label className="text-xs font-medium text-slate">Réf. client / PO</label>
                <input value={billing.po_number} onChange={(e) => setBilling({ ...billing, po_number: e.target.value })} data-testid="billing-po" className={`${inputCls} mt-1 font-mono`} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate">E-mail facturation</label>
                <input value={billing.billing_email} onChange={(e) => setBilling({ ...billing, billing_email: e.target.value })} data-testid="billing-email" className={`${inputCls} mt-1`} placeholder="comptabilité@…" />
              </div>
            </div>
          </Panel>

          {isQuote && (
            <Panel title="Trajet" testId="form-transport">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-slate">Départ</label>
                  <input value={transport.pickup || ""} onChange={t("pickup")} data-testid="transport-pickup" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Destination</label>
                  <input value={transport.dropoff || ""} onChange={t("dropoff")} data-testid="transport-dropoff" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Date</label>
                  <input type="date" value={transport.date || ""} onChange={t("date")} data-testid="transport-date" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Heure</label>
                  <input type="time" value={transport.time || ""} onChange={t("time")} data-testid="transport-time" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Retour (optionnel)</label>
                  <input type="date" value={transport.return_date || ""} onChange={t("return_date")} data-testid="transport-return" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Véhicule</label>
                  <select value={transport.vehicle_category || "business"} onChange={t("vehicle_category")} data-testid="transport-vehicle" className={`${inputCls} mt-1`}>
                    <option value="business">Business Class</option>
                    <option value="first">First Class</option>
                    <option value="van">Van</option>
                  </select></div>
                <div><label className="text-xs font-medium text-slate">Passager</label>
                  <input value={transport.passenger_name || ""} onChange={t("passenger_name")} data-testid="transport-passenger" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Passagers</label>
                  <input type="number" min="1" value={transport.passengers || ""} onChange={t("passengers")} data-testid="transport-passengers" className={`${inputCls} mt-1 font-mono`} /></div>
              </div>
            </Panel>
          )}

          <Panel title="Lignes de facturation" testId="form-lines">
            <LinesEditor lines={lines} onChange={setLines} vatRegistered={settings?.billing?.vat_registered !== false} />
          </Panel>

          <Panel title="Paramètres du document" testId="form-params">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate">Langue du document</label>
                <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="doc-language" className={`${inputCls} mt-1`}>
                  <option value="fr">Français</option><option value="en">English</option><option value="de">Deutsch</option>
                </select>
              </div>
              {isQuote ? (
                <div>
                  <label className="text-xs font-medium text-slate">Validité (jours)</label>
                  <input type="number" min="1" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} data-testid="quote-validity"
                    placeholder={String(settings?.billing?.quote_validity_days || 15)} className={`${inputCls} mt-1 font-mono`} />
                </div>
              ) : (
                <div>
                  <label className="text-xs font-medium text-slate">Échéance</label>
                  <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="invoice-due"
                    className={`${inputCls} mt-1 font-mono`} />
                  <p className="mt-1 text-[11px] text-slate">Vide → délai par défaut ({settings?.billing?.payment_terms_days || 30} j à l&apos;émission)</p>
                </div>
              )}
            </div>
            {isQuote ? (
              <div className="mt-3">
                <label className="text-xs font-medium text-slate">Conditions</label>
                <textarea value={conditions} onChange={(e) => setConditions(e.target.value)} rows={2} data-testid="quote-conditions"
                  className={`${inputCls} mt-1 h-auto py-2.5`} placeholder="Prix fixe TTC, attente aéroport incluse…" />
              </div>
            ) : (
              <div className="mt-3">
                <label className="text-xs font-medium text-slate">Conditions de règlement</label>
                <textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={2} data-testid="invoice-terms-text"
                  className={`${inputCls} mt-1 h-auto py-2.5`} placeholder="Payable sous 30 jours par virement ou Swiss QR…" />
              </div>
            )}
            <div className="mt-3">
              <label className="text-xs font-medium text-slate">Note interne</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="doc-notes"
                className={`${inputCls} mt-1 h-auto py-2.5`} />
            </div>
          </Panel>
        </div>

        <div className="space-y-4">
          <div className="xl:sticky xl:top-24">
            <DocPreview kind={kind} number={id ? "…" : (isQuote ? "QUO-2026-…" : "Brouillon")}
              customer={customer} lines={totals.lines} totals={totals} settings={settings}
              validUntil={isQuote ? `+${validityDays || settings?.billing?.quote_validity_days || 15} j` : null}
              dueDate={!isQuote ? dueDate || null : null}
              serviceInfo={serviceInfo || (isQuote && transport.pickup ? { route: `${transport.pickup} → ${transport.dropoff || "…"}`, ride_date: [transport.date, transport.time].filter(Boolean).join(" · ") } : null)}
              vatRegistered={settings?.billing?.vat_registered !== false} />
            <div className="mt-4 flex items-center gap-3">
              <button type="submit" disabled={busy || locked} data-testid="doc-save"
                className={`${btnPrimary} flex-1 justify-center disabled:opacity-50`}>
                {busy ? "Enregistrement…" : isQuote ? (id ? "Enregistrer le devis" : "Créer le devis") : (id ? "Enregistrer la facture" : "Créer la facture")}
              </button>
              {id && (
                <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/${isQuote ? "quotes" : "invoices"}/${id}/pdf`}
                  target="_blank" rel="noreferrer" data-testid="doc-pdf-preview" className={btnSecondary}>
                  <FileDown className="h-4 w-4" strokeWidth={1.5} /> Aperçu PDF</a>
              )}
            </div>
            <p className="mt-2 text-center text-xs text-slate">
              Aperçu indicatif — le document officiel est généré en PDF (format A4{isQuote ? "" : " + section Swiss QR Bill"}).
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
